import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

import { NAV_OPTIONS } from "./NavOptions";
import useModules from "../hooks/useModules";
import { BRAND_GREEN } from "../theme/colors";

const COLLAPSED_WIDTH = 64;
const EXPANDED_WIDTH = 220;
const TOPNAV_HEIGHT = 64;

const SIDEBAR_BG = "#001e3c";
const ITEM_HOVER_BG = "rgba(255,255,255,0.07)";
const ITEM_SELECTED_BG = "rgba(100,131,21,0.18)";
const TEXT_DEFAULT = "rgba(255,255,255,0.72)";
const TEXT_SELECTED = "#ffffff";

export default function SideNav({ currentPath, onNavigate }) {
  const { hasComponent } = useModules();
  const [expanded, setExpanded] = useState(true);

  const visibleNavOptions = NAV_OPTIONS.filter((item) => {
    if (!item.access) return true;
    if (Array.isArray(item.access)) {
      return item.access.some((component) => hasComponent(component));
    }
    return hasComponent(item.access);
  });

  const handleNavigation = (value) => {
    if (value === "/bre-companies") {
      if (hasComponent("bre.companies")) { onNavigate("/bre-companies"); return; }
      if (hasComponent("prospecting.companies")) { onNavigate("/prospecting-companies"); return; }
      if (hasComponent("bre.membership")) { onNavigate("/membership"); return; }
      onNavigate("/home");
      return;
    }
    if (value === "/bre-contacts") {
      if (hasComponent("bre.contacts")) { onNavigate("/bre-contacts"); return; }
      if (hasComponent("prospecting.contacts")) { onNavigate("/prospecting-contacts"); return; }
      onNavigate("/home");
      return;
    }
    onNavigate(value);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
          transition: "width 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
          overflowX: "hidden",
          boxSizing: "border-box",
          background: SIDEBAR_BG,
          borderRight: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "4px 0 24px rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
          top: `${TOPNAV_HEIGHT}px`,
          height: `calc(100% - ${TOPNAV_HEIGHT}px)`,
        },
      }}
    >
      {/* Toggle button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: expanded ? "flex-end" : "center",
          alignItems: "center",
          px: 1,
          py: 1.25,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <IconButton
          onClick={() => setExpanded(!expanded)}
          size="small"
          sx={{
            color: "rgba(255,255,255,0.88)",
            width: 36,
            height: 36,
            borderRadius: 2,
            transition: "background-color 0.15s ease, color 0.15s ease",
            "&:hover": {
              backgroundColor: ITEM_HOVER_BG,
              color: BRAND_GREEN,
            },
          }}
        >
          {expanded ? <ChevronLeftIcon sx={{ fontSize: 22 }} /> : <ChevronRightIcon sx={{ fontSize: 22 }} />}
        </IconButton>
      </Box>

      {/* Nav items */}
      <List
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          px: 1,
          py: 1.5,
          gap: 0.25,
        }}
      >
        {visibleNavOptions.map(({ label, value, Icon }) => {
          const isSelected =
            value === "/home"
              ? currentPath === "/home"
              : currentPath.startsWith(value);

          return (
            <Tooltip
              key={value}
              title={!expanded ? label : ""}
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: BRAND_GREEN,
                    color: "#ffffff",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 1.5,
                    "& .MuiTooltip-arrow": { color: BRAND_GREEN },
                  },
                },
              }}
            >
              <ListItemButton
                selected={isSelected}
                onClick={() => handleNavigation(value)}
                sx={{
                  minHeight: 42,
                  px: expanded ? 1.25 : 0,
                  py: 0,
                  borderRadius: 2,
                  justifyContent: expanded ? "flex-start" : "center",
                  gap: 1.5,
                  position: "relative",
                  color: isSelected ? TEXT_SELECTED : TEXT_DEFAULT,
                  transition: "background-color 0.15s ease, color 0.15s ease",

                  "&.Mui-selected": {
                    background: ITEM_SELECTED_BG,
                    color: TEXT_SELECTED,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: "18%",
                      bottom: "18%",
                      width: 3,
                      borderRadius: "0 3px 3px 0",
                      backgroundColor: BRAND_GREEN,
                    },
                    "&:hover": {
                      background: "rgba(100,131,21,0.26)",
                    },
                  },

                  "&:hover": {
                    background: ITEM_HOVER_BG,
                    color: TEXT_SELECTED,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: "center",
                    alignItems: "center",
                    color: isSelected ? BRAND_GREEN : "rgba(255,255,255,0.88)",
                    transition: "color 0.15s ease",
                  }}
                >
                  <Icon sx={{ fontSize: 22 }} />
                </ListItemIcon>

                {expanded && (
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontWeight: isSelected ? 700 : 500,
                      fontSize: "0.875rem",
                      lineHeight: 1.2,
                      letterSpacing: "0.01em",
                      color: "inherit",
                      whiteSpace: "nowrap",
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>
    </Drawer>
  );
}
